import { useContext, useEffect, useRef, useSyncExternalStore, type ReactNode, type RefObject } from "react";
import { createPortal } from "react-dom";
import { useLocation } from "react-router-dom";
import { useTranslation } from "@i18n";
import { ThemeContext } from "../../../ProviderApp";
import ButtonComponent from "@CubeComponents/form/ButtonComponent";
import { BUTTON_TYPES } from "@CubeComponents/constants/globalVariable";
import MaintenanceIllustration from "@Assets/icons/svg/MaintenanceIllustration.svg";
import ServerUnreachableIllustration from "@Assets/icons/svg/ServerUnreachableIllustration.svg";
import { formatMaintenanceTime } from "../core/platformStatusModel";
import { webPlatformStatus } from "../core/webPlatformStatus";

const OVERLAY_Z_INDEX = "z-[2147483000]";

/**
 * While a screen is up, everything else on the page is made inert, so keyboard focus cannot reach the
 * app behind it — including MUI modals, which render as siblings of the app root. The app itself stays
 * mounted underneath: nothing the user had open or was typing is lost when the screen lifts.
 */
function useInertBackground(overlay: RefObject<HTMLDivElement | null>, active: boolean) {
  useEffect(() => {
    const element = overlay.current;
    if (!active || !element) return undefined;
    const others = Array.from(document.body.children).filter((node) => node !== element && !node.hasAttribute("inert"));
    others.forEach((node) => node.setAttribute("inert", ""));
    element.focus();
    return () => others.forEach((node) => node.removeAttribute("inert"));
  }, [overlay, active]);
}

interface StatusScreenProps {
  overlayRef: RefObject<HTMLDivElement | null>;
  illustration: string;
  title: string;
  children: ReactNode;
}

function StatusScreen({ overlayRef, illustration, title, children }: Readonly<StatusScreenProps>) {
  const { theme } = useContext(ThemeContext);
  return (
    <div
      ref={overlayRef}
      role="dialog"
      aria-modal="true"
      aria-labelledby="platform-status-title"
      tabIndex={-1}
      className={`fixed inset-0 ${OVERLAY_Z_INDEX} flex overflow-y-auto font-dm-sans outline-none`}
      style={{ background: `var(${theme.rootBackground})`, color: `var(${theme.rootTextColor})` }}
    >
      <div className="m-auto flex w-full max-w-md flex-col items-center gap-4 px-6 py-10 text-center">
        <img src={illustration} alt="" aria-hidden="true" className="w-64 max-w-full" />
        <h1 id="platform-status-title" className="text-2xl font-semibold">
          {title}
        </h1>
        {children}
      </div>
    </div>
  );
}

function PlatformStatusScreens() {
  const { view, checking } = useSyncExternalStore(webPlatformStatus.subscribe, webPlatformStatus.getSnapshot);
  const { t, i18n } = useTranslation();
  const overlayRef = useRef<HTMLDivElement>(null);
  const { screen, staffBypass } = view;
  useInertBackground(overlayRef, screen.kind !== "none");

  let content: ReactNode = null;
  if (screen.kind === "maintenance") {
    content = (
      <StatusScreen
        overlayRef={overlayRef}
        illustration={MaintenanceIllustration}
        title={t("Planned maintenance in progress")}
      >
        <p>{t("We're carrying out planned maintenance. The platform will be available again shortly.")}</p>
        <p className="font-semibold">
          {t("Expected back by {{time}}", { time: formatMaintenanceTime(screen.window.end, i18n.language) })}
        </p>
        {screen.window.reason && <p className="text-sm opacity-75">{screen.window.reason}</p>}
        <p className="text-sm opacity-75">{t("This page will update on its own when we're back.")}</p>
      </StatusScreen>
    );
  } else if (screen.kind === "backend-unreachable") {
    content = (
      <StatusScreen
        overlayRef={overlayRef}
        illustration={ServerUnreachableIllustration}
        title={t("We'll be right back")}
      >
        <p>{t("We're having trouble reaching our servers. Keep this page open — it will reconnect on its own.")}</p>
        <ButtonComponent
          buttonType={BUTTON_TYPES.GRADIENT_BUTTON}
          onClick={webPlatformStatus.checkNow}
          loading={checking}
          disabled={checking}
        >
          {checking ? t("Reconnecting…") : t("Try again")}
        </ButtonComponent>
      </StatusScreen>
    );
  }

  return createPortal(
    <>
      {content}
      {staffBypass && (
        <div
          role="status"
          className={`fixed inset-x-0 bottom-4 ${OVERLAY_Z_INDEX} mx-auto w-fit max-w-[90vw] rounded-full bg-[#1F2937] px-4 py-2 text-center text-sm text-white shadow-lg`}
        >
          {t("Staff access: customers see the maintenance page until {{time}}.", {
            time: formatMaintenanceTime(staffBypass.end, i18n.language),
          })}
        </div>
      )}
    </>,
    document.body,
  );
}

/**
 * Full-page screens for planned maintenance and for our backend being unreachable. A user whose own
 * connection is down gets nothing from here. Not shown to the headless PDF-export renderer, whose pages
 * must print the document rather than a status screen.
 */
function PlatformStatusGate() {
  const { pathname } = useLocation();
  if (pathname.includes("pdfExport")) return null;
  return <PlatformStatusScreens />;
}

export default PlatformStatusGate;
